import React from 'react';
import { Link } from 'react-router-dom';
import { Zap, ExternalLink, Trash2, GitBranch } from 'lucide-react';
import WorkflowPreviewCard from './WorkflowPreviewCard';
import { TRIGGER_TYPES } from '../WorkflowBuilder/nodeTypes';

/**
 * WorkflowConfirmationCard — Interactive card shown after a workflow is built.
 * Shows summary + action buttons: Activate / Edit in Builder / Discard.
 * Buttons trigger follow-up messages to Able.
 *
 * ABLE-31: WorkflowConfirmationCard chat component
 */
export default function WorkflowConfirmationCard({ workflowState, onSendMessage }) {
  const { workflow_id, name, nodes = [], status } = workflowState;

  const triggerTypeSet = new Set(TRIGGER_TYPES.map(t => t.type));
  const triggerNode = nodes.find(n => triggerTypeSet.has(n.type));

  const isActive = status === 'active';

  const handleActivate = () => {
    if (onSendMessage) {
      onSendMessage(`Activate the workflow "${name}"`);
    }
  };

  const handleDiscard = () => {
    if (onSendMessage) {
      onSendMessage(`Delete the workflow "${name}" — I don't need it anymore`);
    }
  };

  return (
    <div className="my-2 rounded-xl border border-gray-200 dark:border-gray-700/80 bg-white dark:bg-gray-800/60 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 dark:border-gray-700/60 bg-gray-50/80 dark:bg-gray-800/40">
        <GitBranch className="w-3.5 h-3.5 text-brand-500 dark:text-brand-400" />
        <span className="text-xs font-medium text-gray-700 dark:text-gray-200 truncate">
          Workflow Ready
        </span>
        {isActive && (
          <span className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-green-100 dark:bg-green-900/40 text-[0.625rem] font-medium text-green-700 dark:text-green-400">
            <span className="w-1 h-1 rounded-full bg-green-500 animate-pulse" />
            Active
          </span>
        )}
      </div>

      {/* Summary */}
      <div className="px-3 py-2.5 space-y-2">
        <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
          <span><span className="font-medium text-gray-700 dark:text-gray-300">{nodes.length}</span> node{nodes.length !== 1 ? 's' : ''}</span>
          {triggerNode && (
            <span>Trigger: <span className="font-medium text-gray-700 dark:text-gray-300">{triggerNode.label}</span></span>
          )}
        </div>

        {/* Inline preview */}
        <WorkflowPreviewCard workflowState={workflowState} />
      </div>

      {/* Actions */}
      {!isActive && (
        <div className="flex items-center gap-2 px-3 py-2 border-t border-gray-100 dark:border-gray-700/60">
          <button
            onClick={handleActivate}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 hover:bg-brand-100 dark:hover:bg-brand-900/50 transition-colors cursor-pointer"
          >
            <Zap className="w-3 h-3" />
            Activate
          </button>
          <Link
            to={`/workflows/${workflow_id}/builder`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/40 transition-colors cursor-pointer"
          >
            <ExternalLink className="w-3 h-3" />
            Edit in Builder
          </Link>
          <button
            onClick={handleDiscard}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors ml-auto cursor-pointer"
          >
            <Trash2 className="w-3 h-3" />
            Discard
          </button>
        </div>
      )}
    </div>
  );
}
